import { API, PolicyNotificationViewDTO } from "@swivl-mono/api-helper";
import { Button, ButtonSize, ButtonVariant } from "@swivl-mono/ui";
import { useCallback, useEffect, useState } from "react";

/**
 * Swivl-admin release screen for policy review notifications (§2.4).
 * Pending rows are staged automatically at server boot when a definition
 * ships with applyMode "review"; releasing creates one call-to-action ticket
 * per policy-builder-flagged org in THIS environment and stamps the row.
 */
export function PolicyNotificationsView(): React.ReactElement {
  const [pending, setPending] = useState<PolicyNotificationViewDTO[]>([]);
  const [sent, setSent] = useState<PolicyNotificationViewDTO[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await API.PolicyNotifications.getNotifications();
      setPending(response.pending);
      setSent(response.sent);
      setLoadError(null);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : String(error));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRelease = useCallback(
    async (notification: PolicyNotificationViewDTO) => {
      const confirmed = window.confirm(
        `Send "${notification.title}" (rev ${notification.revision}) to ${notification.blastRadius ?? 0} org(s)? This creates a ticket per org and cannot be taken back.`
      );
      if (!confirmed) return;
      setSendingId(notification.id);
      setLastResult(null);
      try {
        const result = await API.PolicyNotifications.release(notification.id);
        setLastResult(result.ok ? `Sent — ${result.ticketsCreated} ticket(s) created.` : (result.error ?? "Send failed."));
        await load();
      } catch (error) {
        setLastResult(error instanceof Error ? error.message : String(error));
      } finally {
        setSendingId(null);
      }
    },
    [load]
  );

  return (
    <div className="mx-auto max-w-4xl p-6">
      <h1 className="mb-1 text-2xl font-semibold">Policy Notifications</h1>
      <p className="mb-6 text-sm text-grey-500">
        Definition revisions shipped with applyMode &ldquo;review&rdquo; stage here at deploy. Releasing creates one call-to-action ticket per
        policy-builder org in this environment.
      </p>

      {loadError && <div className="mb-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{loadError}</div>}
      {lastResult && <div className="mb-4 rounded bg-teal-50 px-3 py-2 text-sm text-grey-700">{lastResult}</div>}

      <h2 className="mb-2 text-lg font-semibold">
        Pending
        {pending.length > 0 && (
          <span className="ml-2 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">{pending.length}</span>
        )}
      </h2>
      {pending.length === 0 ? (
        <p className="mb-8 text-sm text-grey-400">Nothing pending.</p>
      ) : (
        <div className="mb-8 space-y-3">
          {pending.map((notification) => (
            <div key={notification.id} className="rounded-lg border border-grey-200 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium">
                    {notification.title} <span className="text-xs text-grey-400">rev {notification.revision}</span>
                  </div>
                  <div className="mt-1 text-sm whitespace-pre-wrap text-grey-600">{notification.releaseNote}</div>
                  <div className="mt-2 text-xs text-grey-400">
                    Staged {new Date(notification.createdAt).toLocaleString()} · would ticket {notification.blastRadius ?? 0} org(s)
                  </div>
                </div>
                <Button
                  variant={ButtonVariant.PRIMARY}
                  size={ButtonSize.SMALL}
                  disabled={sendingId !== null}
                  onClick={() => handleRelease(notification)}
                >
                  {sendingId === notification.id ? "Sending…" : "Send tickets"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <h2 className="mb-2 text-lg font-semibold">Sent</h2>
      {sent.length === 0 ? (
        <p className="text-sm text-grey-400">Nothing sent yet.</p>
      ) : (
        <div className="space-y-2">
          {sent.map((notification) => (
            <div key={notification.id} className="rounded-lg border border-grey-100 bg-grey-50 p-3 text-sm">
              <span className="font-medium">{notification.title}</span> <span className="text-xs text-grey-400">rev {notification.revision}</span>
              <span className="ml-2 text-xs text-grey-500">sent {notification.sentAt ? new Date(notification.sentAt).toLocaleString() : "—"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
